/**
 * Merges downloaded questionnaire template JSON bodies (from
 * `GET …/questionnaire` → S3) into a single `FlowTemplate` the engine walks.
 *
 * Production path: pass templates in API response order — no client-side
 * package filtering. Mock simulation of catalogue order lives in
 * `getMockComposedFlowTemplate.ts` + `estatePackageSectionPlans.ts`.
 */
import type {FlowTemplate, ModalDef, Section} from '../../containers/estatePlanning/resolvers/types';

/** Shape of one downloaded template JSON file (section file or modals file). */
export type EstateTemplateBody = {
  id?: string;
  sections?: Section[];
  modals?: Record<string, ModalDef>;
  addressStateGate?: FlowTemplate['addressStateGate'];
};

/** One entry from `QuestionnaireResponse.templates[]` after its JSON body is downloaded. */
export type DownloadedEstateTemplate = {
  templateId?: string;
  body: EstateTemplateBody;
};

export type ComposeEstateFlowTemplateOptions = {
  /** Flow-level id (e.g. from session metadata or first template). */
  flowTemplateId?: string;
  /** When true, log a console warning if a section file's first section id looks unexpected. */
  warnOnSectionMismatch?: boolean;
};

const DEFAULT_FLOW_TEMPLATE_ID = 'estate_planning';

/**
 * Composes downloaded template bodies in API order: concatenates `sections`,
 * merges `modals`, and copies `addressStateGate` from the first body that
 * declares it (typically about-you).
 */
export function composeEstateFlowTemplateFromDownloads(
  templates: DownloadedEstateTemplate[],
  options: ComposeEstateFlowTemplateOptions = {}
): FlowTemplate {
  const sections: Section[] = [];
  const modals: Record<string, ModalDef> = {};
  let addressStateGate: FlowTemplate['addressStateGate'];

  for (const {templateId, body} of templates) {
    if (body.sections?.length) {
      if (options.warnOnSectionMismatch && process.env.NODE_ENV !== 'production' && templateId) {
        const firstSectionId = body.sections[0]?.id;
        const slugHint = templateId.replace(/-template$/, '').replace(/_/g, '-');
        if (
          firstSectionId &&
          !firstSectionId.includes(slugHint.replace(/-/g, '_')) &&
          slugHint !== 'executor-and-poa'
        ) {
          // eslint-disable-next-line no-console
          console.warn(
            `[estatePlanning] composeEstateFlowTemplateFromDownloads: "${templateId}" first section id "${firstSectionId}" may not match template`
          );
        }
      }
      sections.push(...body.sections);
    }

    if (body.modals) {
      Object.assign(modals, body.modals);
    }

    if (!addressStateGate && body.addressStateGate) {
      addressStateGate = body.addressStateGate;
    }
  }

  return {
    id: options.flowTemplateId ?? DEFAULT_FLOW_TEMPLATE_ID,
    sections,
    modals,
    ...(addressStateGate ? {addressStateGate} : {}),
  };
}
